import { ButtonIcon, FieldGroup, FieldSection, Header, ImageUploader, SelectDateTime } from '@/ui'
import { Controller, useForm } from 'react-hook-form'
import { RollingOfferRead } from '@/api/dashboard'
import { ErrorMessage } from '@hookform/error-message'
import { useTranslation } from 'react-i18next'
import { Button, FormErrorMessage, Input, Select, Textarea, formatHookFormErrors } from '@dashboard/ui'
import { CloseOutline } from '@/icons'
import { Divider } from '@/components'
import { MAX_DESCRIPTION_LENGTH, MAX_NAME_LENGTH } from '@/Settings'
import { validateInterval } from '@/layouts/components/ItemTimelimit'

interface GeneralSettingsProps {
  onClose: () => void
  onApply: (data: RollingOfferRead) => void
  offer: RollingOfferRead
}

export const GeneralSettings = (props: GeneralSettingsProps) => {
  const { t } = useTranslation('offers')
  const { t: tMain } = useTranslation()

  const {
    register,
    formState: { errors, isSubmitting, isDirty },
    setError,
    clearErrors,
    control,
    handleSubmit,
  } = useForm<RollingOfferRead>({
    values: {
      ...props.offer,
    },
  })

  const onSubmit = (data: RollingOfferRead) => {
    if (data.end_at && !props.offer.id && data.end_at < new Date().getTime() / 1000) {
      setError('end_at', { message: tMain('validation.end_at.error') })
      return
    }
    const err = validateInterval(data.start_at, data.end_at, tMain)
    if (err) {
      setError('start_at', { message: err })
      return
    } else {
      clearErrors('start_at')
    }

    props.onClose()
    props.onApply(data)
  }

  const renderGeneral = () => {
    return (
      <FieldSection label={t('general')}>
        <FieldGroup label={t('name')}>
          <Input
            {...register('name', {
              required: tMain('validation.required'),
              maxLength: {
                message: t('validation.maxLength', {
                  field: t('name'),
                  value: MAX_NAME_LENGTH,
                }),
                value: MAX_NAME_LENGTH,
              },
              setValueAs: v => v?.trim(),
            })}
            autoFocus
            {...formatHookFormErrors(errors, 'name')}
          />
        </FieldGroup>
        <FieldGroup label={t('description')}>
          <Textarea
            rows={2}
            {...register('description', {
              required: tMain('validation.required'),
              maxLength: {
                message: tMain('validation.maxLength', {
                  field: t('description'),
                  value: MAX_DESCRIPTION_LENGTH,
                }),
                value: MAX_DESCRIPTION_LENGTH,
              },
              setValueAs: v => v?.trim(),
            })}
            {...formatHookFormErrors(errors, 'description')}
          />
        </FieldGroup>

        <FieldGroup label={t('background_image_url.pattern')}>
          <Controller
            control={control}
            name="settings.background_image_url"
            render={({ field }) => (
              <ImageUploader
                accept={{
                  'image/jpeg': ['.jpeg', '.jpg'],
                  'image/png': [],
                  'image/webp': [],
                }}
                {...field}
              />
            )}
          />
          <ErrorMessage
            name="settings.background_image_url"
            errors={errors}
            render={({ message }) => <FormErrorMessage>{message}</FormErrorMessage>}
          />
        </FieldGroup>
        <FieldGroup label={tMain('background_image.size')}>
          <Controller
            control={control}
            name="settings.background_size"
            render={({ field }) => (
              <Select
                {...field}
                {...formatHookFormErrors(errors, 'settings.background_size')}
                items={[
                  {
                    value: 'cover',
                    children: tMain('background_image.size.cover'),
                  },
                  {
                    value: 'repeat',
                    children: tMain('background_image.size.repeat'),
                  },
                ]}
              />
            )}
          />
          <ErrorMessage
            name="settings.background_size"
            errors={errors}
            render={({ message }) => <FormErrorMessage>{message}</FormErrorMessage>}
          />
        </FieldGroup>
      </FieldSection>
    )
  }

  const renderTimeLimitations = () => {
    return (
      <FieldSection label={t('time-limitations')}>
        <FieldGroup label={tMain('datetime.start_at')}>
          <Controller control={control} name="start_at" render={({ field }) => <SelectDateTime {...field} />} />
          <ErrorMessage
            name="start_at"
            errors={errors}
            render={({ message }) => <FormErrorMessage>{message}</FormErrorMessage>}
          />
        </FieldGroup>
        <FieldGroup label={tMain('datetime.end_at')}>
          <Controller control={control} name="end_at" render={({ field }) => <SelectDateTime {...field} />} />
          <ErrorMessage
            name="end_at"
            errors={errors}
            render={({ message }) => <FormErrorMessage>{message}</FormErrorMessage>}
          />
        </FieldGroup>
      </FieldSection>
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex h-full flex-col bg-fg-primary">
        <Header title={t('general-settings')} className="sticky top-0 z-50 p-4" minWidth="auto">
          <div className="flex items-center gap-4">
            <Button disabled={!isDirty} loading={isSubmitting} type="submit">
              {tMain('apply-changes')}
            </Button>
            <ButtonIcon onClick={props.onClose} type="button" size="xs" variant="outline" color="secondary">
              <CloseOutline />
            </ButtonIcon>
          </div>
        </Header>

        <div className="flex size-full flex-col p-4">
          {renderGeneral()}
          <Divider />

          {renderTimeLimitations()}
        </div>
      </div>
    </form>
  )
}
